/**
 * Shared Facebook Login pieces for the creator flows.
 *
 * Instagram creator/business accounts authenticate through Facebook Login —
 * Meta gives no standalone Instagram OAuth for them, and the backend finds the
 * connected Instagram account by walking the user's Facebook Pages. Both the
 * sign-in screen and the social-settings connect screen run the same dialog,
 * so the scopes and the post-dialog checks live here rather than in either one.
 */

export const FB_VERSION = 'v25.0';

/**
 * The permissions a creator's insights depend on.
 *
 * `instagram_manage_insights` is what authorises the account-level insights
 * endpoint — follower demographics, reach, profile views. We have asked for
 * it since the first Facebook login, which means a creator who unticks it is
 * the difference between real audience data and a bought estimate.
 *
 * `business_management` is not needed by a creator who personally admins
 * their own Page, but is required when the Page sits inside a Business
 * Manager they only have delegated access to — the agency-managed creators
 * we were dropping. One extra line on the consent screen.
 */
const INSTAGRAM_SCOPES = [
  'public_profile',
  'email',
  'pages_show_list',
  'instagram_manage_insights',
  'instagram_basic',
  'pages_read_engagement',
  'business_management',
];

const BRAND_EXTRA_SCOPES = [
  'ads_read',
  'ads_management',
  // Creator Marketplace (Meta) -- makes the consent screen show the permission.
  'pages_manage_metadata',
  'business_management',
  'instagram_creator_marketplace_discovery',
];

/**
 * The scopes whose absence breaks the creator's insights later on. Left out of
 * this list: public_profile and email, which Facebook does not let a user
 * decline separately, and the brand-only ad scopes.
 */
const REQUIRED_GRANTS = [
  'pages_show_list',
  'instagram_basic',
  'instagram_manage_insights',
  'pages_read_engagement',
];

// Requested, but never worth blocking a sign-in over: most creators admin
// their own Page and will never need it.

const GRANT_LABELS = {
  pages_show_list: 'your list of Facebook Pages',
  instagram_basic: 'your Instagram profile',
  instagram_manage_insights: 'your Instagram insights',
  pages_read_engagement: 'your Page engagement',
};

const NO_PAGE =
  'Your Facebook account has no Page. Instagram insights come through a Facebook Page, so create one and link your Instagram professional account to it, then try again.';
const NO_INSTAGRAM =
  'We found your Facebook Page but no Instagram professional account linked to it. Link your Instagram account to the Page in Facebook settings, then try again.';
const PAGES_UNREADABLE = 'We could not read your Facebook Pages. Please try again.';

export function scopesFor(userType) {
  const scopes =
    userType === 'brand' ? INSTAGRAM_SCOPES.concat(BRAND_EXTRA_SCOPES) : INSTAGRAM_SCOPES;
  // business_management is now in both lists; Facebook tolerates a repeat but
  // it reads as sloppy in the consent URL.
  return Array.from(new Set(scopes)).join(',');
}

/**
 * Names the required scopes the creator unticked in the dialog. Facebook only
 * reports grantedScopes when the login was made with return_scopes: true; with
 * no list to read we cannot tell what was declined, so report nothing rather
 * than blocking a creator over a missing field.
 */
function declinedGrants(authResponse) {
  const granted = authResponse && authResponse.grantedScopes;
  if (!granted) return [];
  const grantedList = granted.split(',');
  return REQUIRED_GRANTS.filter((scope) => !grantedList.includes(scope));
}

function declinedGrantsMessage(declined) {
  const labels = declined.map((scope) => GRANT_LABELS[scope] || scope);
  const readable =
    labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels.slice(-1)}`;
  return `We still need access to ${readable}. Please sign in again and leave those switched on — without them we cannot show your post performance to brands.`;
}

/**
 * Reads the Pages behind this login and the Instagram professional account
 * linked to them. The backend does the same walk when it registers the
 * creator, but doing it here first means a creator missing a Page or an
 * Instagram link gets told exactly that instead of a generic sign-in failure.
 *
 * The token is passed explicitly rather than left to the SDK's internal auth
 * state: this runs just after a logout-and-login cycle, and a /me call that
 * picks up the cleared state instead of the fresh token fails with OAuth 2500,
 * "An active access token must be used to query information about the current
 * user."
 */
function fetchInstagramAccount(FB, accessToken) {
  return new Promise((resolve, reject) => {
    FB.api(
      '/me/accounts',
      {
        fields: 'id,name,instagram_business_account{id,username}',
        access_token: accessToken,
      },
      (response) => {
        if (!response || response.error) {
          reject(new Error(PAGES_UNREADABLE));
          return;
        }

        const pages = response.data || [];
        if (pages.length === 0) {
          reject(new Error(NO_PAGE));
          return;
        }

        // A creator can admin several Pages; use the first one that actually
        // has Instagram linked rather than assuming it is the first Page.
        const page = pages.find((p) => p.instagram_business_account);
        if (!page) {
          reject(new Error(NO_INSTAGRAM));
          return;
        }

        resolve({ page, instagramAccount: page.instagram_business_account, pages });
      },
    );
  });
}

/**
 * Everything that has to hold before the backend can register this creator:
 * the permissions they actually granted, then the Page and Instagram account
 * behind them. Resolves to {page, instagramAccount, pages}, or rejects with a
 * message written to be shown to the creator as-is.
 */
export function checkInstagramAccess(FB, authResponse) {
  // This used to be skipped entirely in production, so a creator could untick
  // instagram_manage_insights on the live site and nobody found out — the
  // only symptom was an insights table that never filled.
  const declined = declinedGrants(authResponse);
  if (declined.length > 0) {
    return Promise.reject(new Error(declinedGrantsMessage(declined)));
  }
  return fetchInstagramAccount(FB, authResponse && authResponse.accessToken);
}

/**
 * Runs the Facebook dialog and everything around it: drops the previous grant
 * so a creator can switch accounts and re-grant anything they removed, opens
 * the dialog, then checks what came back.
 *
 * `onSuccess` receives (authResponse, account) where account is the resolved
 * {page, instagramAccount, pages} — null for brands, who connect ad accounts
 * rather than an Instagram creator profile. `onError` receives an Error whose
 * message can be shown to the user as-is, except for 'cancelled', which just
 * means they closed the dialog.
 */
export function startFacebookLogin(FB, { userType, onSuccess, onError }) {
  let opened = false;

  const openDialog = () => {
    // Guarded because the revoke path below can reach this more than once if
    // a Facebook callback fires late as well as via the timeout.
    if (opened) return;
    opened = true;
    FB.login(
      (response) => {
        if (!response || !response.authResponse) {
          if (onError) onError(new Error('cancelled'));
          return;
        }
        if (userType === 'brand') {
          onSuccess(response.authResponse, null);
          return;
        }
        checkInstagramAccess(FB, response.authResponse).then(
          (account) => onSuccess(response.authResponse, account),
          (error) => {
            if (onError) onError(error);
          },
        );
      },
      // return_scopes so a user who unticks a permission in the dialog is
      // named in authResponse.grantedScopes rather than failing silently.
      { scope: scopesFor(userType), return_scopes: true },
    );
  };

  FB.getLoginStatus((status) => {
    const previousToken =
      status && status.authResponse ? status.authResponse.accessToken : null;

    if (!status || status.status !== 'connected' || !previousToken) {
      // No usable session, so there is nothing to revoke — going straight to
      // login also avoids FB.logout() erroring without an access token.
      openDialog();
      return;
    }
    try {
      // Same reason as fetchInstagramAccount: name the token rather than
      // trusting the SDK's internal state, or this 401s with OAuth 2500.
      // A failure here is not worth blocking on — the dialog below still
      // re-asks for anything the user removed.
      FB.api('/me/permissions', 'delete', { access_token: previousToken }, () =>
        FB.logout(() => openDialog()),
      );
    } catch (e) {
      openDialog();
    }
    // Safety net: if either callback never fires, still open the dialog.
    setTimeout(openDialog, 1500);
  });
}
